import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Upload, Plus, AlertTriangle } from "lucide-react";

export default function AddCnModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [includeWithoutGh, setIncludeWithoutGh] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setBusy(true);
    const t = toast.loading("Analizando fichero…");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/catalog/add/preview", fd);
      setPreview(data);
      toast.success(`${data.to_add} CN listos para añadir`, { id: t });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se ha podido leer el fichero", { id: t });
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!file || !preview) return;
    setBusy(true);
    const t = toast.loading("Añadiendo al catálogo…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("include_without_gh", includeWithoutGh ? "true" : "false");
      fd.append("update_existing", updateExisting ? "true" : "false");
      const { data } = await api.post("/admin/catalog/add/apply", fd);
      toast.success(
        `${data.added} CN añadidos` +
        (data.updated ? ` · ${data.updated} corregidos` : "") +
        (data.skipped_existing ? ` · ${data.skipped_existing} ya existían` : "") +
        (data.without_pvl ? ` · ${data.without_pvl} sin PVL` : ""),
        { id: t, duration: 9000 },
      );
      onDone?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al añadir", { id: t });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/60 z-50 flex items-center justify-center p-4" data-testid="add-cn-modal">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-700">Nomenclátor</div>
            <div className="font-display text-lg font-bold text-slate-900">Añadir CN genéricos desde Excel</div>
          </div>
          <button onClick={onClose} data-testid="add-cn-close" className="text-slate-400 hover:text-slate-900">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-600">
            Columnas esperadas: <b>Laboratorio · CN · Producto · CH (G.H.)</b>. Se dan de alta como
            <b> genéricos que entran en estudio</b> y el <b>PVL se toma del G.H.</b> (el precio más frecuente
            entre los genéricos de ese grupo).
          </p>

          <label
            data-testid="add-cn-file"
            className="cursor-pointer flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-md py-6 text-sm font-medium text-slate-600 hover:border-slate-900 hover:bg-slate-50"
          >
            <Upload className="h-4 w-4" />
            {file ? file.name : "Seleccionar Excel o CSV"}
            <input type="file" accept=".csv,.txt,.xlsx,.xls" className="hidden" onChange={pick} />
          </label>

          {preview && (
            <div className="space-y-3" data-testid="add-cn-preview">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  ["Filas", preview.rows],
                  ["Se añaden", preview.to_add],
                  ["Ya existían", preview.already_in_catalog],
                  ["Sin G.H.", preview.without_gh_reference],
                ].map(([k, v]) => (
                  <div key={k} className="border border-slate-200 rounded-md p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{k}</div>
                    <div className="font-mono-num text-lg font-bold text-slate-900">{v}</div>
                  </div>
                ))}
              </div>

              {Object.keys(preview.lab_map || {}).length > 0 && (
                <div className="text-xs text-slate-600 border border-slate-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                    Laboratorios normalizados
                  </div>
                  {Object.entries(preview.lab_map).map(([k, v]) => (
                    <div key={k} className="font-mono-num">{k} → <b>{v}</b></div>
                  ))}
                </div>
              )}

              {preview.already_in_catalog > 0 && (
                <div className="border border-slate-200 rounded-md p-3 text-sm" data-testid="addcn-existing-block">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                    {preview.already_in_catalog} CN ya están en el catálogo
                  </div>
                  <div className="max-h-52 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase text-slate-500">
                        <tr className="text-left">
                          <th className="py-1 pr-2">CN</th>
                          <th className="py-1 pr-2">G.H. actual en catálogo</th>
                          <th className="py-1 pr-2">G.H. del fichero</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.existing_items?.map((it) => {
                          const diff = (it.gh || "") && (it.gh || "").trim().toUpperCase() !== (it.current_gh || "").trim().toUpperCase();
                          return (
                            <tr key={it.cn} className="border-t border-slate-100 align-top">
                              <td className="py-1 pr-2 font-mono-num">{it.cn}</td>
                              <td className="py-1 pr-2">{it.current_gh || <span className="text-slate-400">sin G.H.</span>}</td>
                              <td className={`py-1 pr-2 ${diff ? "text-amber-700 font-semibold" : "text-slate-500"}`}>
                                {it.gh || "—"}{diff ? " ←" : ""}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <label className="flex items-center gap-2 mt-2 text-slate-700">
                    <input
                      type="checkbox"
                      checked={updateExisting}
                      onChange={(e) => setUpdateExisting(e.target.checked)}
                      data-testid="addcn-update-existing"
                    />
                    <span>Corregir las fichas que ya existen con los datos del fichero (actualiza nombre, G.H.,
                    laboratorio y PVL; <b>no borra nada</b>)</span>
                  </label>
                </div>
              )}

              {preview.without_gh_reference > 0 && (
                <div className="border border-amber-300 bg-amber-50 rounded-md p-3 text-sm text-amber-900">
                  <div className="flex gap-2">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <div>
                      <b>{preview.without_gh_reference} CN</b> tienen un G.H. que no existe en el catálogo, así que no
                      hay PVL de referencia. Por defecto se dejan fuera.
                      <ul className="mt-1 list-disc pl-4">
                        {preview.no_gh_items?.slice(0, 5).map((it) => (
                          <li key={it.cn} className="font-mono-num">{it.cn} · {it.gh}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      checked={includeWithoutGh}
                      onChange={(e) => setIncludeWithoutGh(e.target.checked)}
                      data-testid="add-cn-include-nogh"
                    />
                    Añadirlos igualmente, sin PVL
                  </label>
                </div>
              )}

              {preview.sample?.length > 0 && (
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    Muestra de lo que se añadirá
                  </div>
                  <table className="w-full text-xs">
                    <tbody className="divide-y divide-slate-100">
                      {preview.sample.map((it) => (
                        <tr key={it.cn}>
                          <td className="px-3 py-1.5 font-mono-num">{it.cn}</td>
                          <td className="px-3 py-1.5 max-w-[200px] truncate" title={it.name}>{it.name}</td>
                          <td className="px-3 py-1.5 text-slate-500 max-w-[140px] truncate" title={it.gh}>{it.gh}</td>
                          <td className="px-3 py-1.5 font-mono-num text-right">
                            {it.pvl != null ? `${it.pvl} €` : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900">
            Cancelar
          </button>
          <button
            onClick={apply}
            disabled={busy || !preview || (preview.to_add === 0 && !includeWithoutGh)}
            data-testid="add-cn-apply"
            className="inline-flex items-center gap-2 bg-emerald-700 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-emerald-800 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            {busy ? "Procesando…" : "Añadir al catálogo"}
          </button>
        </div>
      </div>
    </div>
  );
}
